import React, { useState, useEffect, useRef } from 'react';
import { ComplaintWizardState } from '../NewComplaintModal';
import styles from './ReviewStep.module.css';
import OrderDetailModal from '../../../orders/components/Pedidos/OrderDetailModal';
import { Order } from '../../../orders/services/ordersAPI';
import CloseIcon from '@mui/icons-material/Close';
// Icons for gallery (Zoom, Download, etc) are now handled in Shared/ImageOverlay
import { formatDocument } from '../../utils/formatters';
import OrderCard from '../Shared/OrderCard/OrderCard';
import ItemCard from '../Shared/ItemCard/ItemCard';
import { ComplaintItem } from '../../services/complaintsAPI';
import ImageOverlay from '../Shared/ImageViewer/ImageOverlay';

interface ReviewStepProps {
    data: ComplaintWizardState;
    updateData: (updates: Partial<ComplaintWizardState>) => void;
    goToStep?: (step: number) => void;
    highlightRefund?: boolean;
}

const ReviewStep: React.FC<ReviewStepProps> = ({ data, updateData, goToStep, highlightRefund }) => {
    const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
    const [previewIndex, setPreviewIndex] = useState<{ index: number, type: 'foto' | 'etiqueta' } | null>(null);
    const handleOpenOrder = () => {
        setIsOrderModalOpen(true);
    };

    const handleCloseOrder = () => {
        setIsOrderModalOpen(false);
    };

    const handleEdit = () => {
        if (goToStep) goToStep(1);
    };

    // --- Image Gallery Logic ---

    const openPreview = (index: number, type: 'foto' | 'etiqueta') => {
        setPreviewIndex({ index, type });
    };

    const closePreview = () => {
        setPreviewIndex(null);
    };

    // --- Validation Scroll Logic ---
    // Removed refundContainerRef and its associated useEffect as dropdown is removed.

    // --- Dropdown Logic ---
    // Removed dropdown states, refs, and logic.

    const refundOptions = [
        { value: 'CREDITO', label: 'Gerar crédito' },
        { value: 'DESCONTO', label: 'Desconto no boleto' },
        { value: 'REPOSICAO', label: 'Reposição' }
    ];

    const currentRefundLabel = refundOptions.find(opt => opt.value === data.tipoRessarcimento)?.label || 'Não Informado';



    // Construct a dummy Order object for the modal
    const dummyOrder: Order = {
        ID: data.order?.id || 0,
        COD_TEOREMA: Number(data.order?.code || 0),
        cliente: data.order?.clientName || '',
        TIPO_PEDIDO: 'N/A',
        EMISSAO: data.order?.emissionDate || '',
        PREVISAO: '',
        SAIDA: null,
        NOTA_FISCAL: null,
        SITUACAO: 'Faturado',
    } as unknown as Order;

    const activePreviewImages = previewIndex?.type === 'foto' ? data.images : data.labelImages;

    return (
        <div className={styles.container}>
            <OrderCard order={data.order}>
                <button className={styles.editSectionButton} onClick={handleEdit}>
                    Editar Pedido
                </button>
                <button className={styles.editSectionButton} onClick={handleOpenOrder}>
                    Ver Pedido Completo
                </button>
            </OrderCard>

            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h3 className={styles.sectionTitle}>Itens da Reclamação ({data.items.length})</h3>
                    <button className={styles.editSectionButton} onClick={() => goToStep?.(2)}>
                        Editar Itens
                    </button>
                </div>
                {data.items.length === 0 ? <p className={styles.label}>Nenhum item adicionado.</p> : (
                    <div className={styles.itemList}>
                        {data.items.map((item: ComplaintItem) => (
                            <ItemCard
                                key={item.id}
                                item={item}
                                readOnly={true}
                            />
                        ))}
                    </div>
                )}
            </div>

            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h3 className={styles.sectionTitle}>Tipo de Ressarcimento</h3>
                    <button className={styles.editSectionButton} onClick={() => goToStep?.(2)}>
                        Alterar
                    </button>
                </div>
                <div>
                    <p className={styles.value} style={{ fontSize: '1rem', color: 'var(--color-primary)', fontWeight: 600 }}>{currentRefundLabel}</p>
                </div>
            </div>

            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h3 className={styles.sectionTitle}>Fotos do Produto ({data.images.length})</h3>
                    <button className={styles.editSectionButton} onClick={() => goToStep?.(3)}>
                        Editar Fotos
                    </button>
                </div>
                {data.images.length === 0 ? <p className={styles.label}>Nenhuma foto adicionada.</p> : (
                    <div className={styles.imagePreviewList}>
                        {data.images.map((file: File, idx: number) => (
                            <img
                                key={idx}
                                src={URL.createObjectURL(file)}
                                alt={`Evidence ${idx}`}
                                className={styles.miniThumb}
                                onClick={() => openPreview(idx, 'foto')}
                                title="Clique para ampliar"
                            />
                        ))}
                    </div>
                )}
            </div>

            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h3 className={styles.sectionTitle}>Fotos da Etiqueta ({data.labelImages.length})</h3>
                    <button className={styles.editSectionButton} onClick={() => goToStep?.(3)}>
                        Editar Fotos
                    </button>
                </div>
                {data.labelImages.length === 0 ? <p className={styles.label}>Nenhuma etiqueta adicionada.</p> : (
                    <div className={styles.imagePreviewList}>
                        {data.labelImages.map((file: File, idx: number) => (
                            <img
                                key={idx}
                                src={URL.createObjectURL(file)}
                                alt={`Label ${idx}`}
                                className={styles.miniThumb}
                                onClick={() => openPreview(idx, 'etiqueta')}
                                title="Clique para ampliar"
                            />
                        ))}
                    </div>
                )}
            </div>

            {isOrderModalOpen && (
                <OrderDetailModal
                    order={dummyOrder}
                    onClose={handleCloseOrder}
                />
            )}



            {/* Shared Image Viewer Overlay */}
            {previewIndex !== null && activePreviewImages.length > 0 && (
                <ImageOverlay
                    images={activePreviewImages}
                    initialIndex={previewIndex.index}
                    onClose={closePreview}
                />
            )}

        </div>
    );
};

export default ReviewStep;